import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { SelectComponent } from "@karma-games/angular-devkit/modules/controls-components";
import { IStatisticsLabel } from "./statistics-container/interfaces";
import { GetStatisticsUseCase } from "./domain/get-statistics.use-case";
import { Subject, takeUntil } from "rxjs";
import { EStatisticsTab, EStatisticsTimespan } from "./domain/enums";
import { labelCategoryMap, statisticsPanelTabs, topBetsTimespanOptions } from "./constants";
import { ChangeStatisticsTabUseCase } from "./domain/set-statistics-category.use-case";
import IOption = SelectComponent.IOption;
import { IRoundBet } from "../core/round/interfaces";
import { GetTotalBetsCountUseCase } from "./domain/get-total-bets-count.use-case";
import { RequestNewMyBetsPageUseCase } from "./domain/request-new-my-bets-page.use-case";
import { map } from "rxjs/operators";
import { IUser, GetUserUseCase } from "@karma-games/angular-devkit/src/lib/modules/user";

@Component({
    selector: "statistics-panel",
    templateUrl: "./statistics-panel.component.html",
    styleUrls: ["./statistics-panel.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatisticsPanelComponent implements OnInit, OnDestroy {
    @Output() onTabChanged: EventEmitter<IOption> = new EventEmitter<IOption>();
    public tabs: IOption[] = statisticsPanelTabs;
    public currentTab: IOption = this.tabs[0];
    public labels: IStatisticsLabel[] = [];
    public statisticsList: IRoundBet[] = [];
    public EStatisticsTab: typeof EStatisticsTab = EStatisticsTab;
    public topBetsTimespanOptions: IOption<EStatisticsTimespan>[] = topBetsTimespanOptions;
    public topBetsTimespanValue: IOption<EStatisticsTimespan> = this.topBetsTimespanOptions[0];
    public totalBets: number = 0;
    public loaderImg: string = "assets/images/ui-elements/loader_apng.png";
    public loaderOn: boolean = false;
    private user: IUser | undefined = undefined;
    private destroy$: Subject<void> = new Subject();

    constructor(private getStatisticsUseCase: GetStatisticsUseCase,
                private changeStatisticsTabUseCase: ChangeStatisticsTabUseCase,
                private getTotalBetsCountUseCase: GetTotalBetsCountUseCase,
                private requestNewMyBetsPageUseCase: RequestNewMyBetsPageUseCase,
                private getUserUseCase: GetUserUseCase,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.getUserUseCase.execute()
            .pipe(takeUntil(this.destroy$))
            .subscribe((user: IUser) => {
                this.user = user;
                this.statisticsList = this.statisticsList.map(item => {
                    if (item.user?.id.toString() === this.user?.id) {
                        return {
                            ...item,
                            user: this.user ?? item.user
                        }
                    } else {
                        return item;
                    }
                });
                this.cdr.detectChanges();
            });

        this.getTotalBetsCountUseCase.execute()
            .pipe(takeUntil(this.destroy$))
            .subscribe((totalBets: number) => {
                this.totalBets = totalBets;
                this.cdr.detectChanges();
            });

        this.getStatisticsUseCase.execute()
            .pipe(map((items: IRoundBet[]) => items.map(item => {
                if (item.user?.id.toString() === this.user?.id) {
                    return {
                        ...item,
                        user: {
                            ...item.user,
                            avatarUrl: this.user?.avatarUrl
                        }
                    };
                } else {
                    return item;
                }
            })), takeUntil(this.destroy$))
            .subscribe((statistics: IRoundBet[]) => {
                this.statisticsList = statistics;
                this.labels = labelCategoryMap[this.currentTab.value];
                this.loaderOn = false;
                this.cdr.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    onTabChange(tab: IOption): void {
        this.currentTab = tab;
        switch (this.currentTab.value) {
            case EStatisticsTab.TopBets:
                this.changeStatisticsTabUseCase.execute(this.currentTab.value, this.topBetsTimespanValue.value);
                break;
            case EStatisticsTab.AllBets:
            case EStatisticsTab.MyBets:
            default:
                this.topBetsTimespanValue = this.topBetsTimespanOptions[0];
                this.changeStatisticsTabUseCase.execute(this.currentTab.value);
                break;
        }
        this.onTabChanged.emit(this.currentTab);
    }

    onTopBetsTimespanValueChange(option: IOption<EStatisticsTimespan>): void {
        this.topBetsTimespanValue = option;
        this.changeStatisticsTabUseCase.execute(this.currentTab.value, this.topBetsTimespanValue.value);
    }

    trackByFn(index: number, item: IRoundBet): string {
        return item.id ?? index.toString();
    }

    requestNewBetsPageUseCase = async (): Promise<void> => {
        if (this.currentTab && this.currentTab.value === EStatisticsTab.MyBets && !this.loaderOn) {
            try {
                this.loaderOn = true;
                this.cdr.detectChanges();
                await this.requestNewMyBetsPageUseCase.execute();
            } catch (e) {
                this.loaderOn = false;
                this.cdr.detectChanges();
            }
        }
    };
}
